// Number and date formatting shared by the Market panels — Story #6837 Stream 8.
// Money is shown compactly (A$140.5bn); official statistics keep the unit the
// source published them in, so a unit this file does not know is printed as-is.

const CURRENCY_PREFIX: Record<string, string> = { AUD: 'A$', USD: 'US$', EUR: '€', JPY: '¥', GBP: '£', CNY: 'CN¥' }

export const currencyPrefix = (code: string): string => CURRENCY_PREFIX[code] ?? `${code} `

/** 17601077429749 → "17.6tn"; 140502000000 → "140.5bn"; 2500000 → "2.5m". */
export function compact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1e12) return `${(value / 1e12).toFixed(1)}tn`
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}bn`
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}m`
  return Math.round(value).toLocaleString('en-AU')
}

export const money = (value: number, prefix: string): string => `${prefix}${compact(value)}`

/** A published statistic in the unit its source used: US$17.6tn, A$140.5bn, or "138.0bn XDC (national currency)". */
export function statValue(r: { value: number; unit: string }): string {
  if (r.unit === 'USD') return money(r.value, 'US$')
  const m = /^([A-Z]{3}) (million|billion)$/.exec(r.unit)
  if (m) return money(r.value * (m[2] === 'million' ? 1e6 : 1e9), currencyPrefix(m[1]))
  return `${compact(r.value)} ${r.unit}`
}

/** "A$90 – 110bn": both ends in the unit of the larger one, trailing .0 dropped. */
export function rangeText(low: number, high: number, prefix: string): string {
  const [div, suffix] = high >= 1e12 ? [1e12, 'tn'] : high >= 1e9 ? [1e9, 'bn'] : high >= 1e6 ? [1e6, 'm'] : [1, '']
  const fmt = (v: number) => String(Number((v / div).toFixed(1)))
  return `${prefix}${fmt(low)} – ${fmt(high)}${suffix}`
}

/** "14 Oct 2026" from epoch seconds or an ISO / date string; null when unparseable. */
export function dateLabel(when: number | string | undefined): string | null {
  if (when === undefined) return null
  const d = typeof when === 'number' ? new Date(when * 1000) : new Date(when)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}
